import { PageType } from "../config/page.types";
import { UserPermissionDTO, UserPermissionsListDTO } from "../datasources/api-client/data-contracts";
import { ApiRoutes } from "../datasources/networking/apiRoutes";
import { TransportPerspective } from "../state/preferences";
import { ApiPermissions, FeatureIds, FeatureKey, FeatureVariantIds, HttpMethod } from "./apiPermissions";
import { PagePermissions, PageRequirement } from "./pagePermissions";

/**
 * Features used by many pages (dropdown options, geocoding, current user).
 * They are ignored by canAccessPage: owning them is not enough to access a page.
 */
const SupportingFeatures: FeatureKey[] = ["UtilsReadOptions", "ServicesGeocoding", "CurrentUser"];

/**
 * Returns the user permissions as a plain list.
 * The backend sends an array, the generated contract describes an object: both are accepted.
 * Used by usePermissions.
 *
 * @param permissions the stored user permissions
 * @returns           the permissions list
 */
export const toPermissionsList = (
	permissions: UserPermissionsListDTO | UserPermissionDTO[] | undefined
): UserPermissionDTO[] => (Array.isArray(permissions) ? permissions : (permissions?.permissions ?? []));

/**
 * Returns the feature required by an API call, read from ApiPermissions.
 *
 * @param route  the API route
 * @param method the HTTP method
 * @returns      the feature, or null if the call needs no feature
 */
export const getRequiredFeature = (route: ApiRoutes, method: HttpMethod): FeatureKey | null =>
	ApiPermissions[route][method] ?? null;

/**
 * Checks if the user owns a feature.
 * With a perspective, features split by perspective require that exact variant.
 *
 * @param userPermissions the user permissions
 * @param feature         the feature to check
 * @param perspective     the transports perspective, if relevant
 * @returns               true if the user owns the feature
 */
export const hasFeature = (
	userPermissions: UserPermissionDTO[],
	feature: FeatureKey,
	perspective?: TransportPerspective
): boolean => {
	const variantId = perspective ? FeatureVariantIds[feature]?.[perspective] : undefined;

	return userPermissions.some(
		(permission) =>
			permission.id === FeatureIds[feature] && (variantId === undefined || permission.variantId === variantId)
	);
};

/**
 * Checks if the user can make an API call.
 * Used by useReadResource / useWriteResource to compute isForbidden.
 *
 * @param userPermissions the user permissions
 * @param route           the API route
 * @param method          the HTTP method
 * @param perspective     the transports perspective of the call, if any
 * @returns               true if the call needs no feature or the user owns it
 */
export const canCall = (
	userPermissions: UserPermissionDTO[],
	route: ApiRoutes,
	method: HttpMethod,
	perspective?: TransportPerspective
): boolean => {
	const feature = getRequiredFeature(route, method);
	return feature === null || hasFeature(userPermissions, feature, perspective);
};

// Returns the feature of a page requirement (API call or explicit feature).
const toFeature = (requirement: PageRequirement): FeatureKey | null =>
	"feature" in requirement ? requirement.feature : getRequiredFeature(requirement.route, requirement.method);

/**
 * Checks if the user can access a page, based on its content features (see PagePermissions).
 * The user needs at least one of them; pages without content features are always accessible.
 * Used by the side menu and by Protected (route guard).
 *
 * @param userPermissions the user permissions
 * @param pageType        the page to check
 * @param perspective     if set, perspective variants are checked for that perspective
 * @returns               true if the page is accessible
 */
export const canAccessPage = (
	userPermissions: UserPermissionDTO[],
	pageType: PageType,
	perspective?: TransportPerspective
): boolean => {
	const features = PagePermissions[pageType]
		.map(toFeature)
		.filter((feature): feature is FeatureKey => feature !== null && !SupportingFeatures.includes(feature));

	return features.length === 0 || features.some((feature) => hasFeature(userPermissions, feature, perspective));
};

/**
 * Page contents (by name) and the models (query results) they rely on.
 */
export type LockableContent = {
	models: Array<{ isForbidden: boolean }>;
	names: string[];
};

/**
 * Returns the names of the contents hidden because one of their models is forbidden.
 * Used by pages to fill the ContentLocked banner.
 *
 * @param contents the page contents and their models
 * @returns        the names of the hidden contents
 */
export const getLockedContents = (contents: LockableContent[]): string[] => {
	const lockedContents = contents.filter(({ models }) => models.some((model) => model.isForbidden));
	return lockedContents.flatMap(({ names }) => names);
};
